import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { BreadcrumbSchema } from '../../components/SchemaMarkup';
import { destinations, regions } from '../../data/destinations';
import './Places.css';

// The source strings mix the code with a conversion rate, e.g. "Thai Baht (1 THB = 0.027 USD)"
const currencyCode = (value = '') => (value.match(/\b[A-Z]{3}\b/) || [value.split(' ')[0]])[0];
const trimParenthetical = (value = '') => value.split(' (')[0].trim();

const Places = () => {
  const [query, setQuery] = useState('');
  const [region, setRegion] = useState('All');

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return destinations.filter((d) => {
      const matchesRegion = region === 'All' || d.region === region;
      if (!matchesRegion) return false;
      if (!q) return true;
      return (
        d.name.toLowerCase().includes(q) ||
        d.region.toLowerCase().includes(q) ||
        d.shortDescription.toLowerCase().includes(q) ||
        (d.bestTime || '').toLowerCase().includes(q)
      );
    });
  }, [query, region]);

  const isFiltered = query.trim() !== '' || region !== 'All';

  const clear = () => {
    setQuery('');
    setRegion('All');
  };

  return (
    <div className="places-page">
      <Helmet>
        <title>Travel Places Guide - Asia &amp; Middle East | Safarnamah</title>
        <meta name="description" content="Explore our comprehensive travel guides for Dubai, Thailand, Vietnam, Sri Lanka, Indonesia, and more. Get visa info, packing tips, and best times to visit." />
        <meta name="keywords" content="asia travel guide, dubai travel, thailand guide, vietnam travel, bali travel, visa requirements, travel tips, when to visit, what to pack" />
      </Helmet>
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://safarnamah.in' },
        { name: 'Destinations', url: 'https://safarnamah.in/places' },
      ]} />

      <section className="places-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="eyebrow">The atlas</span>
            <h1>Everywhere we&rsquo;ve been</h1>
            <p>
              {destinations.length} countries, written up properly &mdash; what things cost,
              when to go, and what we&rsquo;d do differently.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="filter-bar">
        <div className="container filter-bar-in">
          <div className="search">
            <FaSearch aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a country, region or month"
              aria-label="Search destinations"
            />
          </div>

          <div className="chips" role="group" aria-label="Filter by region">
            <button
              type="button"
              className={`chip ${region === 'All' ? 'on' : ''}`}
              aria-pressed={region === 'All'}
              onClick={() => setRegion('All')}
            >
              All regions
            </button>
            {regions.map((r) => (
              <button
                key={r}
                type="button"
                className={`chip ${region === r ? 'on' : ''}`}
                aria-pressed={region === r}
                onClick={() => setRegion(r)}
              >
                {r}
              </button>
            ))}
          </div>

          <span className="count" aria-live="polite">
            <b>{results.length}</b> {results.length === 1 ? 'guide' : 'guides'}
          </span>
        </div>
      </div>

      <section className="places-content">
        <div className="container">
          {results.length === 0 ? (
            <div className="empty">
              <h2>Nothing matches that yet</h2>
              <p>
                We&rsquo;ve only written up places we&rsquo;ve actually been, so the atlas is
                deliberately short. Try a different region.
              </p>
              <button type="button" className="btn btn-secondary" onClick={clear}>
                Clear filters
              </button>
            </div>
          ) : (
            <>
              {isFiltered && (
                <button type="button" className="clear-inline" onClick={clear}>
                  <FaTimes aria-hidden="true" /> Clear filters
                </button>
              )}
              <div className="places-grid">
                {results.map((country, index) => (
                  <motion.article
                    key={country.id}
                    className="place-card"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: Math.min(index, 5) * 0.06 }}
                    viewport={{ once: true, amount: 0.2 }}
                  >
                    <Link to={`/places/${country.id}`} className="place-card-link">
                      <div className="place-image">
                        <img src={country.image} alt={country.name} loading="lazy" />
                        <span className="place-region">{country.region}</span>
                      </div>
                      <div className="place-body">
                        <h2>{country.name}</h2>
                        <p>{country.shortDescription}</p>
                        <dl className="place-facts">
                          <div>
                            <dt>Best time</dt>
                            <dd>{trimParenthetical(country.bestTime)}</dd>
                          </div>
                          <div>
                            <dt>Currency</dt>
                            <dd>{currencyCode(country.currency)}</dd>
                          </div>
                        </dl>
                        <span className="place-cta">Read the guide &nbsp;&rarr;</span>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Places;
